import React from "react";
import styled, { keyframes, css } from "styled-components";

// Styles
import colors from "styles/colors";

const highlightPulse = keyframes`
  0% { background-color: #fff3e0; border-color: #ff9800; }
  50% { background-color: #fff8f0; border-color: #ffb74d; }
  100% { background-color: #fff3e0; border-color: #ff9800; }
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  height: fit-content;
  display: flex;
  flex-direction: column;
  
  ${props => props.highlighted && css`
    animation: ${highlightPulse} 1.5s ease-in-out 2;
    background-color: #fff3e0;
    border-color: #ff9800;
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);
  `}
  
  &:hover {
    border-color: ${colors.primary[100]};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const CardHeader = styled.div`
  margin-bottom: 16px;
  flex: 1;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0;
  line-height: 1.4;
  flex: 1;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const StatusBadge = styled.div`
  flex-shrink: 0;
`;

const Badge = styled.span`
  background-color: ${colors.secondary[500]};
  color: ${colors.background.white};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const TitleMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const QuestionCount = styled.span`
  background: ${colors.primary[50]};
  color: ${colors.primary[600]};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid ${colors.primary[100]};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const AuthorAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
`;

const AuthorText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.gray};
`;

const SurveyTypeContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const SurveyType = styled.span`
  background-color: ${colors.neutral[200]};
  color: ${colors.text.gray};
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const SurveyCard = ({ survey, onSelect, highlighted }) => {
  const getAuthorInitials = (name) => {
    if (!name) return "IL";
    return name.split(" ").map(word => word[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <Card onClick={onSelect} highlighted={highlighted}>
      <CardHeader>
        <TitleContainer>
          <Title>{survey.title}</Title>
        </TitleContainer>

        <TitleMetadata>
          <QuestionCount>
            {survey.questions.length} question
            {survey.questions.length !== 1 ? "s" : ""}
          </QuestionCount>
        </TitleMetadata>

        <Description>{survey.description}</Description>
      </CardHeader>

      <CardFooter>
        <AuthorInfo>
          <AuthorAvatar>
            {getAuthorInitials(survey?.creator || "Dr. Danielle Prowse")}
          </AuthorAvatar>
          <AuthorText>by {survey?.creator || "Dr. Danielle Prowse"}</AuthorText>
        </AuthorInfo>

        <SurveyTypeContainer>
          <SurveyType>{survey.type}</SurveyType>
        </SurveyTypeContainer>
      </CardFooter>
    </Card>
  );
};

export default SurveyCard; 